import { create, tsx } from '@dojo/framework/core/vdom';
import * as css from './Hero.m.css';
import { RenderResult } from '@dojo/framework/core/interfaces';
import { ConnectButton } from './ConnectButton';
import { Text } from './Text';
import ContentWrapper from './ContentWrapper';
import { BackgroundKey, HeroBackground } from './HeroBackground';

export interface HeroChildren {
	subtitle?: RenderResult;
	title?: RenderResult;
	xlTitle?: RenderResult;
	blurb?: RenderResult;
}

export interface HeroProperties {
	connect?: boolean;
	/** applies small top and bottom padding */
	smaller?: boolean;
	/** sm: 112px, md: 172px - default (no value) is 216px */
	paddingTop?: 'sm' | 'md';
	/** xs: 72px, sm: 80px, md: 144px - default (no value) is 192px */
	paddingBottom?: 'xs' | 'sm' | 'md';
	background?: BackgroundKey;
	align?: 'center' | 'left' | 'right';
	/** default=sm; sm: 12px, md: 32px */
	titlePaddingBottom?: 'none' | 'sm' | 'md';
	/** default=sm; sm: 608px, md: 832px */
	blurbWidth?: 'sm' | 'md';
}

const factory = create().children<HeroChildren>().properties<HeroProperties>();

export const Hero = factory(function Hero({ children, properties }) {
	const [{ subtitle, title, xlTitle, blurb }] = children();
	const {
		connect,
		smaller,
		background,
		align = 'left',
		blurbWidth,
		titlePaddingBottom = 'md',
		paddingBottom,
		paddingTop
	} = properties();

	let alignCss = undefined;
	if (align === 'right') {
		alignCss = css.alignRight;
	}
	if (align === 'center') {
		alignCss = css.alignCenter;
	}

	const body = (
		<div
			classes={[
				css.content,
				alignCss,
				paddingBottom === 'xs' && css.bottomPaddingXSmall,
				(paddingBottom === 'sm' || smaller) && css.bottomPaddingSmall,
				(paddingTop === 'sm' || smaller) && css.topPaddingSmall,
				paddingBottom === 'md' && css.bottomPaddingMedium,
				paddingTop === 'md' && css.topPaddingMedium,
				!!title && css.smallerMargin
			]}
		>
			<div
				classes={[
					titlePaddingBottom === 'md' && css.titlePaddingBottomMedium,
					titlePaddingBottom === 'sm' && css.titlePaddingBottomSmall
				]}
			>
				{title && (
					<Text type="hero-subhead" align={align === 'center' ? 'center' : undefined}>
						{title}
					</Text>
				)}
				{xlTitle && (
					<Text
						type="hero-primary"
						responsive="callout-head"
						align={align === 'center' ? 'center' : undefined}
					>
						{xlTitle}
					</Text>
				)}
				{subtitle && (
					<div classes={[css.subtitle]}>
						<Text type="hero-subhead" align={align === 'center' ? 'center' : undefined}>
							{subtitle}
						</Text>
					</div>
				)}
			</div>
			{blurb && (
				<div classes={[css.blurb, blurbWidth === 'md' && css.blurbWidthMedium]}>
					<Text type="large-body" responsive="body">
						{blurb}
					</Text>
				</div>
			)}
			{connect && (
				<span classes={css.buttonWrapper}>
					<ConnectButton />
				</span>
			)}
		</div>
	);

	return (
		<div classes={[css.root]}>
			{background && <HeroBackground background={background} />}
			<div classes={[css.wrapper]}>
				<ContentWrapper>{body}</ContentWrapper>
			</div>
		</div>
	);
});

export default Hero;
